import { BRAND_CODE } from "@/config/common";
import { MarketConfiguration } from "@/config/markets/const";
import type { CustomProduct } from "@apptus/esales-api";
import isExploded from "./isExploded";

export enum PriceType {
  standard = "standard",
  campaign = "campaign",
  pricematch = "pricematch",
  rrp = "rrp",
}

export default function getPriceInfoFromProduct(
  product: CustomProduct,
  marketConfig: MarketConfiguration,
) {
  const identifier = `${BRAND_CODE}_${marketConfig.code}`;
  const customAttributes = product.custom ?? {};
  const customNumbers = product.typedCustom?.numbers ?? {};

  if (!isExploded(product)) {
    const [fromPriceSame] =
      customAttributes[`${identifier}_fromPriceSame`] ?? [];
    const [fromPriceType] =
      customAttributes[`${identifier}_fromPriceType`] ?? [];
    const fromPrice = fromPriceSame?.id !== "YES";
    const priceType =
      (fromPriceType?.id as keyof typeof PriceType) ?? PriceType.standard;

    const price = customNumbers[`${identifier}_fromPrice`];
    const fromPriceRrp = customNumbers[`${identifier}_fromPriceRRP`];
    const fromPrice30Days = customNumbers[`${identifier}_fromPrice30Days`];

    return {
      fromPrice,
      priceType,
      price: price,
      priceRrp: fromPriceRrp,
      price30Days: fromPrice30Days,
    };
  }

  // Raw cmp30 attribute, no fallback — resolveLowestPrice30Days (used by the
  // PDP) holds the display policy (zero-guard, product-level fallback).
  // Neither current consumer destructures price30Days, but if one starts to,
  // keep the two in sync or the same product will show two different figures.
  const lowestPrice30Days = customNumbers[`cmp30_${identifier}`];
  const recommendedPrice = customNumbers[`rrp_${identifier}`];

  const [variant] = product.variants;

  const bestPrice = variant?.prices.find(
    (price) => price.id === `${identifier}_best`,
  );
  const rrpPrice = variant?.prices.find(
    (price) => price.id === `${identifier}_RRP`,
  );
  const campaignPrice = variant?.prices.find(
    (price) => price.id === `${identifier}_campaign`,
  );
  const pricematchPrice = variant?.prices.find(
    (price) => price.id === `${identifier}_pricematch`,
  );

  const priceType = campaignPrice
    ? PriceType.campaign
    : pricematchPrice
      ? PriceType.pricematch
      : rrpPrice
        ? PriceType.rrp
        : PriceType.standard;

  return {
    fromPrice: false,
    priceType,
    price: bestPrice?.sellingPrice,
    priceRrp: recommendedPrice,
    price30Days: lowestPrice30Days,
  };
}
